import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { ROLE_LABELS } from "@/lib/constants";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Menu, X, LogOut, LayoutDashboard, Search, Newspaper, UserCircle } from "lucide-react";

const NAV = [
  { to: "/cerca", label: "Cerca tratta", icon: Search },
  { to: "/news", label: "News", icon: Newspaper },
];

export function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const onLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/70 border-b border-border">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-home-link"><Logo size={30} /></Link>

        <nav className="hidden md:flex items-center gap-1">
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} data-testid={`nav-${n.to.slice(1)}-link`}
              className={`px-3 py-2 text-sm font-medium rounded-sm transition-colors hover:text-primary ${location.pathname === n.to ? "text-primary" : "text-muted-foreground"}`}>
              {n.label}
            </Link>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="rounded-sm gap-2" data-testid="user-menu-trigger">
                  <UserCircle size={18} /> {user.name?.split(" ")[0] || "Account"}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <div className="px-2 py-1.5 text-xs text-muted-foreground">
                  {user.email}<br /><span className="font-mono-plex">{ROLE_LABELS[user.role]}</span>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-dashboard">
                  <LayoutDashboard size={16} className="mr-2" /> Pannello
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onLogout} data-testid="menu-logout">
                  <LogOut size={16} className="mr-2" /> Esci
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Button variant="ghost" className="rounded-sm" onClick={() => navigate("/accedi")} data-testid="nav-login-btn">Accedi</Button>
              <Button className="rounded-sm bg-accent hover:bg-accent/90 text-accent-foreground font-semibold" onClick={() => navigate("/accedi?tab=register")} data-testid="nav-register-btn">Registrati</Button>
            </>
          )}
        </div>

        <button className="md:hidden p-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-border bg-background px-4 py-3 space-y-1">
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} onClick={() => setOpen(false)} className="flex items-center gap-2 px-2 py-2.5 text-sm rounded-sm hover:bg-muted" data-testid={`mnav-${n.to.slice(1)}`}>
              <n.icon size={16} /> {n.label}
            </Link>
          ))}
          <div className="pt-2 flex flex-col gap-2">
            {user ? (
              <>
                <Button variant="outline" className="rounded-sm justify-start" onClick={() => { setOpen(false); navigate("/dashboard"); }}>Pannello</Button>
                <Button variant="ghost" className="rounded-sm justify-start" onClick={onLogout}>Esci</Button>
              </>
            ) : (
              <>
                <Button variant="outline" className="rounded-sm" onClick={() => { setOpen(false); navigate("/accedi"); }}>Accedi</Button>
                <Button className="rounded-sm bg-accent text-accent-foreground" onClick={() => { setOpen(false); navigate("/accedi?tab=register"); }}>Registrati</Button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
